'use client'

import {usePathname} from 'next/navigation'

import IconHome from '@/icons/home.svg'
import Link from "next/link";
import {useQuery} from "@/hooks/useQuery";
import {getProducts} from "@/api/actions/products";
import {useContext} from "react";
import {ProductFamily} from "@/models/productFamily";
import {ProductGroup} from "@/models/productGroup";
import {useTranslations} from "@/contexts/TranslationContext";
import {useSystemSettings} from "@/hooks/useSystemSettings";

const capitalize = (s: string) => (s && s[0].toUpperCase() + s.slice(1)) || ''

interface BreadcrumbItemProps {
    currentPath?: string;
    pathNames: string[];
    index: number;
    home?: boolean;
    item: {
        title: string;
        url: string;
        active: boolean;
    };
}

const BreadcrumbItem = ({ currentPath, pathNames, index, home = false, item }: BreadcrumbItemProps) => {
    const { unitSystem, scopeSystem } = useSystemSettings();

    const url = home ? '/' : `/${pathNames.slice(0, index + 1).join('/')}`

    const title = home ? 'Home' : capitalize(item.title)
    const active = currentPath === url
    const currentRoute = usePathname();
    const locale = currentRoute.split("/")[1];
    const spacerClasses = !home ? ' before:ml-2 before:mr-2 before:content-["/"]' : ''
    const activeClasses = active ? ' text-primary' : ''

    return (
        <li className={`breadcrumb__item flex flex-row items-center${spacerClasses}`}>
            <Link className={`breadcrumb__link inline-flex flex-row items-center${activeClasses} hover:text-primary`} href={`/${locale}/${url}?scopeSystem=${scopeSystem}&unitSystem=${unitSystem}`}>
                {home && (
                    <span className='breadcrumb__icon mr-3 h-5 w-5'>
            <IconHome className='h-5 w-5' />
          </span>
                )}
                <span className='breadcrumb__title'>{title}</span>
            </Link>
        </li>
    )
}

interface BreadcrumbComponentProps {
    language: string;
    region: string;
    families?: ProductFamily[];
    groups?: ProductGroup[];
}

/**
 * families and groups are used to convert IDs to names.
 * @param ProductFamily[] families ProductFamily array
 * @param ProductGroup[] groups ProductGroup array
 * @constructor
 */
const BreadcrumbComponent = ({ language, region, families, groups }: BreadcrumbComponentProps) => {
    const {unitSystem, scopeSystem} = useSystemSettings();

    const paths = usePathname()
    const pathNames = paths.split('/').filter((path) => path)
    pathNames.shift()
    const currentPath = `/${pathNames.join('/')}`

    const t = useTranslations();

    const { data: productsResponse } = useQuery({
        queryFn: async () => await getProducts(language, 1, 1, region, unitSystem!, scopeSystem!, [], [], [], [], [], [], [Number(pathNames[3])]),
        queryKey: [pathNames[3], language, region, unitSystem, scopeSystem],
        enabled: !!pathNames[3]
    })

    if(productsResponse?.error) return null;

    const product = productsResponse?.data?.Products[0];

    return (
        <section className='breadcrumb hidden md:block'>
            <div className='container py-4'>
                <ul className='breadcrumb__list flex list-none flex-row flex-wrap items-center text-sm text-gray-beige-dark'>
                    <BreadcrumbItem
                        key={1}
                        index={0}
                        pathNames={pathNames}
                        home={true}
                        item={{
                            title: 'Home',
                            url: '/',
                            active: false
                        }}
                    />
                    {pathNames.length > 0 &&
                        pathNames.map((item, index) => {
                            let displayName = {
                                products: t["header.products"],
                                accessories: t["header.accessory"],
                                search: t["header.search"],
                                projects: t["header.projects"],
                            }[item] ?? item;

                            if(paths.includes("product")) {
                                if (index === 1) {
                                    displayName = families?.find(family => family.ID === parseInt(item))?.Name ?? item;
                                } else if (index === 2) {
                                    displayName = groups?.find(group => group.ID === parseInt(item))?.Name ?? item;
                                } else if (index === 3) {
                                    displayName = product?.ArticleNumber ?? "";
                                }
                            }

                            return (
                                <BreadcrumbItem
                                    key={index + 1}
                                    index={index}
                                    currentPath={currentPath}
                                    pathNames={pathNames}
                                    item={{
                                        title: displayName,
                                        url: item,
                                        active: false
                                    }}
                                />
                            )
                        })}
                </ul>
            </div>
        </section>
    )
}

export { BreadcrumbComponent }
